export type CatalogueSort =
  | 'recents'
  | 'release-date'
  | 'score'
  | 'popularity'
  | 'budget'
  | 'box-office';
export const CATALOGUE_MIN_VOTES = 10;
export interface CatalogueFilters {
  title: string;
  minScore: number;
  maxAgeMs: number;
  includeGenres: string[];
  excludeGenres: string[];
  sort: CatalogueSort;
}
export interface CatalogueFilterFields {
  title: string;
  genres: readonly string[];
  releaseDate?: number;
  voteAverage?: number;
  voteCount?: number;
  popularity?: number;
  budget?: number;
  revenue?: number;
  firstPubTs: number;
}
export function filterCatalogue<T>(
  items: readonly T[],
  filters: CatalogueFilters,
  fields: (item: T) => CatalogueFilterFields,
  now = Date.now(),
): T[] {
  const title = filters.title.toUpperCase();
  const sixMonths = 6 * 30 * 24 * 3600 * 1000;
  const values = items
    .map((item, index) => ({item, index, data: fields(item)}))
    .filter(
      ({data}) =>
        data.title.toUpperCase().includes(title) &&
        (filters.minScore === 0 ||
          ((data.voteCount ?? 0) >= CATALOGUE_MIN_VOTES &&
            data.voteAverage !== undefined &&
            data.voteAverage * 10 >= filters.minScore)) &&
        now - (data.releaseDate ?? 0) <= filters.maxAgeMs &&
        (filters.includeGenres.length === 0 ||
          filters.includeGenres.some(genre => data.genres.includes(genre))) &&
        !filters.excludeGenres.some(genre => data.genres.includes(genre)),
    );
  values.sort((left, right) => {
    const a = left.data;
    const b = right.data;
    let order = 0;
    if (filters.sort === 'recents') {
      const recentA = a.releaseDate !== undefined && now - a.releaseDate <= sixMonths;
      const recentB = b.releaseDate !== undefined && now - b.releaseDate <= sixMonths;
      order =
        Number(recentB) - Number(recentA) ||
        (recentA && recentB
          ? b.firstPubTs - a.firstPubTs
          : (b.releaseDate ?? 0) - (a.releaseDate ?? 0));
    } else if (filters.sort === 'release-date') order = (b.releaseDate ?? 0) - (a.releaseDate ?? 0);
    else if (filters.sort === 'score')
      order =
        ((b.voteCount ?? 0) >= CATALOGUE_MIN_VOTES ? (b.voteAverage ?? 0) : 0) -
        ((a.voteCount ?? 0) >= CATALOGUE_MIN_VOTES ? (a.voteAverage ?? 0) : 0);
    else if (filters.sort === 'popularity') order = (b.popularity ?? 0) - (a.popularity ?? 0);
    else if (filters.sort === 'budget') order = (b.budget ?? 0) - (a.budget ?? 0);
    else if (filters.sort === 'box-office') order = (b.revenue ?? 0) - (a.revenue ?? 0);
    return order || left.index - right.index;
  });
  return values.map(value => value.item);
}
